/**
 * COBRA continuation-of-coverage API.
 *
 * Wraps the `/api/v1/cobra/*` endpoints that drive:
 *   * the COBRA dashboard (list + summary tiles),
 *   * the offboarding modal's pre-flight quote,
 *   * admin actions on a single event (record election + payment,
 *     resend notice).
 */
import client from "./client";

export type CobraStatus =
  | "pending_notice"
  | "notice_sent"
  | "elected"
  | "declined"
  | "expired"
  | "active"
  | "terminated";

export interface CobraElectionLine {
  plan_id: string | null;
  plan_name: string | null;
  plan_type: string | null;
  carrier_name: string | null;
  tier: string;
  base_monthly_cost: number;
  monthly_premium: number;
  status: string;
  election_id: string | null;
}

export interface CobraEvent {
  id: string;
  employee_id: string;
  employee_name: string | null;
  qualifying_event_type: string;
  event_date: string | null;
  jurisdiction: string;
  coverage_duration_months: number;
  monthly_premium: number;
  base_premium: number;
  plan_summary: string | null;
  /** Per-plan breakdown — one entry per benefit_election the
   *  employee held at offboard time. The dashboard renders these
   *  as rows under each event. */
  elections: CobraElectionLine[];
  election_deadline: string | null;
  initial_payment_deadline: string | null;
  coverage_start_date: string | null;
  coverage_end_date: string | null;
  status: CobraStatus;
  notice_sent_at: string | null;
  elected_at: string | null;
  declined_at: string | null;
  paid_through_date: string | null;
  notes: string | null;
  created_at: string | null;
}

export interface CobraSummary {
  active_count: number;
  pending_election_count: number;
  /** Manual-review rows — events created from an offboarding where
   *  the rules engine couldn't auto-resolve a jurisdiction. The
   *  notice has NOT been auto-sent yet; admin reviews + clicks
   *  Send Notice from the dashboard. Optional for back-compat with
   *  older deploys. */
  pending_notice_count?: number;
  elected_awaiting_payment_count: number;
  monthly_liability: number;
  late_payment_count: number;
  expiring_30_count: number;
  next_election_deadline: string | null;
}

export interface CobraQuote {
  eligible: boolean;
  jurisdiction: string | null;
  qualifying_event: string | null;
  coverage_duration_months: number | null;
  monthly_premium: number;
  base_premium: number;
  plan_summary: string | null;
  election_deadline: string | null;
  initial_payment_deadline: string | null;
  coverage_start_date: string | null;
  coverage_end_date: string | null;
  employer_employee_count: number;
  rule_id: string | null;
  reason_if_ineligible: string | null;
  /** Active benefit_elections for the employee. Empty when no
   *  benefits data is on file — admin can decide whether to send
   *  COBRA anyway. */
  elections?: {
    plan_id: string | null;
    plan_name: string | null;
    plan_type: string | null;
    carrier_name: string | null;
    tier: string;
    base_monthly_cost: number;
    monthly_premium: number;
  }[];
}

export async function getCobraSummary(): Promise<CobraSummary> {
  const res = await client.get<CobraSummary>("/cobra/summary");
  return res.data;
}

export async function listCobraEvents(opts?: {
  status?: CobraStatus;
}): Promise<CobraEvent[]> {
  const res = await client.get<CobraEvent[]>("/cobra/events", {
    params: opts,
  });
  return res.data;
}

export async function getCobraEvent(eventId: string): Promise<CobraEvent> {
  const res = await client.get<CobraEvent>(`/cobra/events/${eventId}`);
  return res.data;
}

export async function previewCobraForOffboarding(
  employeeId: string,
  opts?: { event_date?: string; termination_reason?: string },
): Promise<CobraQuote> {
  const res = await client.get<CobraQuote>(
    `/cobra/preview/${employeeId}`,
    { params: opts },
  );
  return res.data;
}

export async function recordCobraElection(
  eventId: string,
  elected: boolean,
): Promise<CobraEvent> {
  const res = await client.post<CobraEvent>(
    `/cobra/events/${eventId}/elect`,
    { elected },
  );
  return res.data;
}

export interface CobraPaymentRequest {
  amount: number;
  period_start: string;
  period_end: string;
  method?: string;
  confirmation_code?: string;
}

export async function recordCobraPayment(
  eventId: string,
  payload: CobraPaymentRequest,
): Promise<{
  id: string;
  amount: number;
  period_start: string;
  period_end: string;
  received_at: string;
}> {
  const res = await client.post(
    `/cobra/events/${eventId}/payment`,
    payload,
  );
  return res.data;
}

export async function resendCobraNotice(eventId: string): Promise<CobraEvent> {
  const res = await client.post<CobraEvent>(
    `/cobra/events/${eventId}/resend-notice`,
  );
  return res.data;
}

/**
 * Admin escape hatch: create COBRA events for every terminated
 * employee in this org that doesn't already have one. Called from
 * the "Sync from terminations" button on the COBRA page when
 * automatic creation didn't fire (botched deploy, missing migration,
 * etc.). Always safe — idempotent on existing rows.
 */
export async function backfillCobraFromTerminations(): Promise<{
  created: number;
  skipped_existing: number;
  failed: number;
  total_terminated: number;
  /** Populated when the backend hit an error path. The route always
   *  returns 200 with a structured payload, so callers should check
   *  this field rather than wrap the call in try/catch. */
  error?: string | null;
}> {
  // Hits the minimal /cobra/sync endpoint that uses raw SQL for
  // maximum resilience. The legacy /cobra/backfill-from-terminations
  // route is kept around but no longer the primary entry point.
  const res = await client.post("/cobra/sync");
  return res.data;
}
